/**
 * Persistence for the `AppSidebar` accordion, mirroring the `sidebar_state`
 * cookie that the expand/collapse toggle writes: the server reads it while
 * rendering, so the sections come back from SSR in the state the user left
 * them in, with no open-then-close flash on hydration.
 *
 * We store the set of *closed* sections rather than the open ones, so that a
 * section added to the nav later starts open for everyone instead of being
 * hidden by a cookie written before it existed.
 *
 * Plain module (no `"use client"`), so a server component can read and parse
 * the cookie without pulling the sidebar's client code over the boundary.
 */

export const SIDEBAR_SECTIONS_COOKIE_NAME = "sidebar_sections_closed";

/** Ten years, matching `SIDEBAR_COOKIE_MAX_AGE` in `sidebar.tsx`. */
export const SIDEBAR_SECTIONS_COOKIE_MAX_AGE = 60 * 60 * 24 * 365 * 10;

/** Read a cookie value into the list of closed section ids. */
export function parseClosedSectionIds(
	value: string | undefined | null,
): string[] {
	if (!value) {
		return [];
	}

	return value.split(",").flatMap((rawId) => {
		try {
			const id = decodeURIComponent(rawId.trim());
			return id ? [id] : [];
		} catch {
			// A hand-edited or truncated cookie can hold a fragment that is not
			// valid percent-encoding (`%`). The server reads this cookie while
			// rendering, so a throw here would take out the whole page: drop the
			// entry instead and keep the rest of the preference.
			return [];
		}
	});
}

/** Write the list of closed section ids back into a cookie value. */
export function serializeClosedSectionIds(ids: readonly string[]): string {
	return ids.map(encodeURIComponent).join(",");
}
